import axios from "axios";
import keycloak from "./keycloak";
import authTokenUtils from "./utils/authTokenUtils.cjs";

const apiClient = axios.create({
  baseURL: '/api',  // Proxy path defined in vue.config.js
});

const disableAuthForE2E = process.env.VUE_APP_DISABLE_AUTH === "true";
const STORAGE_COMPANY_ID = "mkzugferd.currentCompanyId";
const STORAGE_E2E_BEARER_TOKEN = "mkzugferd.e2eBearerToken";
let authRecoveryInProgress = false;
const { buildBearerAuthorization, shouldTriggerAuthRecovery } = authTokenUtils;

// Intercept requests to inject the token (disabled for Playwright/E2E mode).
if (!disableAuthForE2E) {
  apiClient.interceptors.request.use(
    async (config) => {
      // Ensure token is refreshed, but never emit malformed bearer headers.
      await keycloak.updateToken(30).catch(() => undefined);

      // Add the keycloak access token only if it looks like a JWT.
      // This prevents backend errors like IDX14100 (token has no dots).
      const token = keycloak.token;
      const authorizationHeader = buildBearerAuthorization(token);
      if (authorizationHeader) {
        config.headers.Authorization = authorizationHeader;
      } else {
        delete config.headers.Authorization;
        // Force a clean login flow once when the local auth state is broken.
        if (shouldTriggerAuthRecovery(token, authRecoveryInProgress)) {
          authRecoveryInProgress = true;
          try {
            await keycloak.login({ redirectUri: window.location.href });
          } catch (_) {
            // The caller handles the final request error.
          } finally {
            authRecoveryInProgress = false;
          }
        }
      }

      // Tenant scoping. The backend resolves the current company from this header (optional) or from persisted user settings.
      const companyId = localStorage.getItem(STORAGE_COMPANY_ID);
      if (companyId) {
        config.headers["X-Company-Id"] = companyId;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
} else {
  // Keep tenant header behavior even in E2E mode (tests may set localStorage and stub API calls).
  apiClient.interceptors.request.use((config) => {
    const bearerToken = localStorage.getItem(STORAGE_E2E_BEARER_TOKEN);
    if (bearerToken) {
      config.headers.Authorization = `Bearer ${bearerToken}`;
    }
    const companyId = localStorage.getItem(STORAGE_COMPANY_ID);
    if (companyId) {
      config.headers["X-Company-Id"] = companyId;
    }
    return config;
  });
}

apiClient.interceptors.response.use(response => response, error => {
  if (error?.response?.data?.code === "company_selection_invalid") {
    window.dispatchEvent(new Event("company-selection-invalid"));
  }
  if (error?.response?.data?.code === "administrative_mfa_required") {
    window.dispatchEvent(new Event("administrative-mfa-required"));
  }
  if (error?.response?.data?.code === "FeatureDisabled") {
    window.dispatchEvent(new Event("company-feature-disabled"));
  }
  return Promise.reject(error);
});
export default apiClient;
